'use client';

import { TierBadge } from '../../billing/TierBadge';
import { cn } from '@/lib/cn';
import type { AuthSession } from '../../../lib/authSession';
import type { BillingExposure } from '../../../hooks/useBillingExposure';
import type { ThemeMode } from '../../ThemeToggle';

type Translate = (key: string) => string;

type UserMenuPopoverProps = {
  session: AuthSession | null;
  billingExposure: BillingExposure;
  tNav: Translate;
  tCommon: Translate;
  tBilling: Translate;
  themeMode: ThemeMode;
  onClose: () => void;
  onSelectThemeMode: (mode: ThemeMode) => void;
  onOpenProfile: () => void;
  onOpenSubscribeModal: () => void;
  onOpenSubscriptionModal: () => void;
  onOpenExecutionEnvironment: () => void;
  onOpenOnboardingGuide: () => void;
  onOpenFeedback: () => void;
  onOpenAdminMenu: () => void;
  onLogout: () => void;
};

const themeOptions: { value: ThemeMode; labelKey: string }[] = [
  { value: 'SYSTEM', labelKey: 'themeSystem' },
  { value: 'LIGHT', labelKey: 'themeLight' },
  { value: 'DARK', labelKey: 'themeDark' },
];

const ChevronIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 text-[var(--color-fg-subtle)]"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 text-[var(--color-fg-subtle)]"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

export const UserMenuPopover = ({
  session,
  billingExposure,
  tNav,
  tCommon,
  tBilling,
  themeMode,
  onClose,
  onSelectThemeMode,
  onOpenProfile,
  onOpenSubscribeModal,
  onOpenSubscriptionModal,
  onOpenExecutionEnvironment,
  onOpenOnboardingGuide,
  onOpenFeedback,
  onOpenAdminMenu,
  onLogout,
}: UserMenuPopoverProps) => {
  const wrap = (handler: () => void) => () => {
    onClose();
    handler();
  };

  const itemClass = cn(
    'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm',
    'text-[var(--color-fg-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-fg)]',
  );

  return (
    <div className="w-60 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2 shadow-xl">
      <div className="px-2 py-1.5">
        <p className="text-[11px] uppercase tracking-wider text-[var(--color-fg-subtle)]">{tNav('signedInUser')}</p>
        <p className="mt-0.5 truncate text-xs font-semibold text-[var(--color-fg)]">
          {session?.member.nickname ?? tCommon('guest')}
        </p>
      </div>

      <div className="my-1 border-t border-[var(--color-border)]" />

      <div className="px-2 py-1.5">
        <p className="mb-1.5 text-[11px] uppercase tracking-wider text-[var(--color-fg-subtle)]">{tNav('theme')}</p>
        <div className="grid grid-cols-3 gap-1">
          {themeOptions.map((option) => {
            const isSelected = themeMode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={isSelected}
                className={cn(
                  'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft-bg)] text-[var(--color-primary)]'
                    : 'border-transparent text-[var(--color-fg-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-fg)]',
                )}
                onClick={() => onSelectThemeMode(option.value)}
              >
                {tNav(option.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="my-1 border-t border-[var(--color-border)]" />

      <button type="button" className={itemClass} onClick={wrap(onOpenProfile)}>
        <span>{tNav('profileSettings')}</span>
        <TierBadge
          tier={session?.member.tier ?? 'FREE'}
          subscriptionStatus={billingExposure.subscriptionStatus}
          loading={billingExposure.subscriptionLoading}
        />
      </button>

      {session?.member.tier === 'PRO' ? (
        <button type="button" className={itemClass} onClick={wrap(onOpenSubscriptionModal)}>
          <span>{tBilling('nav.subscription')}</span>
          <ChevronIcon />
        </button>
      ) : (
        <button type="button" className={itemClass} onClick={wrap(onOpenSubscribeModal)}>
          <span>{tBilling('nav.upgrade')}</span>
          <ChevronIcon />
        </button>
      )}

      {session?.member.isSystemAdmin ? (
        <>
          <div className="my-1 border-t border-[var(--color-border)]" />
          <button type="button" className={itemClass} onClick={wrap(onOpenAdminMenu)}>
            <span>{tNav('adminMenu')}</span>
            <ChevronIcon />
          </button>
        </>
      ) : null}

      <div className="my-1 border-t border-[var(--color-border)]" />

      <button type="button" className={itemClass} onClick={wrap(onOpenExecutionEnvironment)}>
        <span>{tNav('executionEnvironment')}</span>
        <ChevronIcon />
      </button>

      <div className="my-1 border-t border-[var(--color-border)]" />

      <button type="button" className={itemClass} onClick={wrap(onOpenOnboardingGuide)}>
        <span>{tNav('onboardingGuide')}</span>
        <ExternalLinkIcon />
      </button>
      <a href="/?preview=true" target="_blank" rel="noopener noreferrer" className={itemClass} onClick={onClose}>
        <span>{tNav('landingPage')}</span>
        <ExternalLinkIcon />
      </a>

      <div className="my-1 border-t border-[var(--color-border)]" />

      <button type="button" className={itemClass} onClick={wrap(onOpenFeedback)}>
        <span>{tNav('sendFeedback')}</span>
        <ChevronIcon />
      </button>

      <div className="my-1 border-t border-[var(--color-border)]" />

      <button
        type="button"
        className={cn(itemClass, 'text-[var(--color-error)] hover:text-[var(--color-error)]')}
        onClick={wrap(onLogout)}
      >
        <span>{tNav('logout')}</span>
      </button>
    </div>
  );
};
